import React from 'react';
import { makeStyles, Box, Typography } from '@material-ui/core';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { getImageFile } from 'utils/stringUtils';
import { getFormattedTimeFromMinutes } from 'utils/timeUtils';
import AboutThePerson from 'components/AboutThePerson';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import Mixpanel from 'utils/mixpanelService';
import {
  LayersIcon,
  CalendarIcon,
  FlagIcon,
  HeartIcon,
  YogaMeditationIcon,
  LightBulbIcon,
} from './icons';

const useStyles = makeStyles(theme => ({
  title: {
    ...theme.typography.pRegular,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  video: {
    width: '100%',
  },
  richText: {
    '& p, & span[role="button"]': {
      fontSize: props => props.fontSize,
    },
  },
  heading: {
    marginRight: 8,
    [theme.breakpoints.down('xs')]: {
      marginBottom: 8,
    },
  },
  videoContainer: {
    padding: props => (props.isMobile ? theme.spacing(2) : 30),
    borderRadius: '8px !important',
    background: 'linear-gradient(81deg, #D2F4FD -23.74%, #D2FAE9 122.1%)',
  },
  imageContainer: {
    maxWidth: props => props.isMobile && 80,
    marginBottom: theme.spacing(2),
  },
  image: {
    borderRadius: 20,
    maxWidth: '100%',
  },
  imageTitle: {
    ...theme.typography.pRegular,
    textAlign: 'center',
  },
  moduleTitle: {
    ...theme.typography.pRegular,
    fontWeight: 700,

    '& span': {
      ...theme.typography.pRegular,
    },
  },
  label: {
    ...theme.typography.pxSmall,
  },
  featureItem: {
    overflow: 'hidden',
    '& + &': {
      marginTop: theme.spacing(2),
    },
  },
  modulesList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',

    '& li': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  dot: {
    display: 'inline-flex',
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.contrastText,
    marginRight: theme.spacing(2),
  },
  whatToExpect: {
    gap: props => (props.isMobile ? theme.spacing(2) : theme.spacing(3)),
  },
  column: {
    overflow: 'hidden',
  },
  topicsContainer: {
    gap: props => props.isMobile && theme.spacing(2),
  },
  topicItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginRight: props => !props.isMobile && theme.spacing(4),
    width: props => (props.isMobile ? 'calc(33.33333% - 16px)' : 97),
  },
}));

const CourseAbout = ({
  course,
  coursesSiteCopy,
  resourceItemPageSiteCopy,
  fontSize,
  headingVariant,
  isMobile,
  enrollButton,
}) => {
  const classes = useStyles({
    fontSize,
    isMobile,
  });

  const getModuleTimeLabel = courseModule => {
    const time = _get(courseModule.fields, 'moduleResources', []).reduce(
      (total, next) => total + Number(_get(next.fields, 'time', 0)),
      0,
    );

    if (!time) return null;

    const formattedTime = getFormattedTimeFromMinutes({
      totalMinutes: time,
      hourLabel: _get(resourceItemPageSiteCopy, 'pageCopy.hour'),
      hoursLabel: _get(resourceItemPageSiteCopy, 'pageCopy.hours'),
      minuteLabel: _get(
        resourceItemPageSiteCopy,
        'pageCopy.durationLabelSingular',
      ),
      minutesLabel: _get(
        resourceItemPageSiteCopy,
        'pageCopy.durationLabelPlural',
      ),
    });

    return `(${formattedTime})`;
  };

  const onVideoPlay = () => {
    Mixpanel.track('Course Intro Video - Play', {
      course: course.fields.title,
    });
  };

  const onVideoPause = e => {
    const { currentTime, duration } = e.target;
    const percentage = Math.round((currentTime / duration) * 100);
    Mixpanel.track('Course Intro Video - Pause', {
      course: course.fields.title,
      'resource progress': `${percentage}%`,
    });
  };

  const detailsList = [
    {
      icon: LayersIcon,
      title: _get(coursesSiteCopy, 'pageCopy.numberOfModules'),
    },
    {
      icon: CalendarIcon,
      title: _get(coursesSiteCopy, 'pageCopy.numberOfWeeks'),
    },
    {
      icon: LightBulbIcon,
      title: _get(coursesSiteCopy, 'pageCopy.numberOfPractices'),
    },
    {
      icon: YogaMeditationIcon,
      title: _get(coursesSiteCopy, 'pageCopy.numberOfWorksheets'),
    },
    {
      icon: HeartIcon,
      title: _get(coursesSiteCopy, 'pageCopy.reflectAfterModule'),
    },
    {
      icon: FlagIcon,
      title: _get(coursesSiteCopy, 'pageCopy.nextSteps'),
    },
  ];

  const firstColumnItems = detailsList.slice(0, 3);
  const secondColumnItems = detailsList.slice(3, 6);

  const courseTopics = _get(course, 'fields.courseTopics', []);
  const video = _get(course.fields, 'courseVideo');
  const courseAuthors = _get(course, 'fields.authors', []);

  return (
    <Box>
      <Box mb={3}>
        <Box mb={3}>
          <Typography
            color="textPrimary"
            variant={headingVariant}
            className={classes.heading}
          >
            {_get(coursesSiteCopy, 'pageCopy.aboutCourse')}
          </Typography>
        </Box>
        <RichTextReactRenderer
          className={classes.richText}
          data={_get(course, 'fields.description')}
        />
      </Box>
      {video && (
        <Box mb={3} className={classes.videoContainer}>
          <video
            className={classes.video}
            controls
            onPlay={onVideoPlay}
            onPause={onVideoPause}
          >
            <source
              src={video.fields.file.url}
              type={video.fields.file.contentType}
            />
          </video>
        </Box>
      )}
      {enrollButton && <Box mb={3}>{enrollButton}</Box>}
      <Box mb={3}>
        <Box mb={3}>
          <Typography color="textPrimary" variant={headingVariant}>
            {_get(coursesSiteCopy, 'pageCopy.whatToExpect')}
          </Typography>
        </Box>
        <Box display="flex" flexWrap="wrap" className={classes.whatToExpect}>
          <Box className={classes.column}>
            {firstColumnItems.map(item => (
              <Box
                key={item.title}
                display="flex"
                alignItems="center"
                className={classes.featureItem}
              >
                <Box mr={2}>
                  <item.icon />
                </Box>
                <Typography color="textPrimary" className={classes.title}>
                  {item.title}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box className={classes.column}>
            {secondColumnItems.map(item => (
              <Box
                key={item.title}
                display="flex"
                alignItems="center"
                className={classes.featureItem}
              >
                <Box mr={2}>
                  <item.icon />
                </Box>
                <Typography color="textPrimary" className={classes.title}>
                  {item.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
      <Box mb={3}>
        <Box mb={3}>
          <Typography color="textPrimary" variant={headingVariant}>
            {_get(coursesSiteCopy, 'pageCopy.modules', 'Modules')}
          </Typography>
        </Box>
        <ul className={classes.modulesList}>
          {_get(course.fields, 'modules', []).map(courseModule => (
            <li key={courseModule.sys.id}>
              <span className={classes.dot} />
              <Typography
                variant="body1"
                className={classes.moduleTitle}
                color="textPrimary"
              >
                {courseModule.fields.title}{' '}
                <span>{getModuleTimeLabel(courseModule)}</span>
              </Typography>
            </li>
          ))}
        </ul>
      </Box>
      {!_isEmpty(courseTopics) && (
        <Box mb={3}>
          <Box mb={3}>
            <Typography color="textPrimary" variant={headingVariant}>
              {_get(coursesSiteCopy, 'pageCopy.topicsCover')}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexWrap="wrap"
            className={classes.topicsContainer}
          >
            {courseTopics.map(topic => {
              const imageUrl = getImageFile(topic.fields);
              const topicTitle = _get(topic, 'fields.title');

              return (
                <div key={topicTitle} className={classes.topicItem}>
                  <div className={classes.imageContainer}>
                    <img
                      src={imageUrl}
                      alt={topicTitle}
                      className={classes.image}
                    />
                  </div>
                  <Typography
                    color="textPrimary"
                    className={classes.imageTitle}
                  >
                    {topicTitle}
                  </Typography>
                </div>
              );
            })}
          </Box>
        </Box>
      )}
      {!_isEmpty(courseAuthors) && (
        <Box>
          <AboutThePerson
            data={courseAuthors}
            heading={_get(coursesSiteCopy, 'pageCopy.courseAuthors')}
            pageData={[course]}
            showFavoriteIcon={false}
            openPersonInNewTab
          />
        </Box>
      )}
    </Box>
  );
};

export default CourseAbout;
